/**
 * Single source of truth for the Sparte → form-definition and Sparte → wizard
 * family mapping. Consumed by both `ClaimForm.getFormDefinition` (which formdef
 * to render) and `buildClaimWizardSteps` (which curated broker step sequence to
 * build). Keeping one table prevents the two from drifting apart.
 *
 * Form keys map to the `fdefForm*` imports in ClaimForm. Families drive the
 * schmidt two-phase broker phase: 'kfz' | 'haftpflicht' | 'sach'.
 */

// Sparte string → form key. Groups mirror the historic getFormDefinition switch.
const SPARTE_FORM_ENTRIES = {
  A: ["Dienstreisekasko"],
  B: [
    "BBB",
    "Kasko",
    "KFZ - Haftpflicht",
    "KFZ - Haftpflicht (11 %)",
    "KFZ - Haftpflicht (schwer)",
    "KFZ - Schutzbrief",
    "KFZ - Schutzbrief (11 %)",
    "KFZ - Teilkasko",
    "KFZ - Teilkasko (11 %)",
    "KFZ - Teilkasko (schwer)",
    "KFZ - Vollkasko / Teilkasko (11 %)",
    "KFZ - Vollkasko / Teilkasko (schwer)",
    "KFZ - Vollkasko/Teilkasko",
    "KfZPolice-classic",
    "Kfz-Schaden",
  ],
  C: [
    "Feuer",
    "Feuer-Betriebsunterbrechungsversicherung",
    "Feuer-Gebäude",
    "Feuer-Inhalt",
    "Gebäude (Feuer)",
    "Inhalt (Feuer)",
    "Mietverlust (Feuer)",
    "unbenannte Gefahren",
    "Feuer-Rohbau",
  ],
  D: ["Leitungswasser"],
  E: ["Einbruchdiebstahl"],
  F: ["Sturm/Hagel"],
  G: ["Elementar"],
  H: ["Sonstige Gefahren (Gebäude)", "Sonstige Gefahren (Inhalt)"],
  I: [
    "Bauherrenhaftpflicht",
    "Berufshaftpflicht",
    "Betreiber-Haftpflicht",
    "Betreiberhaftpflicht",
    "Betriebshaftpflicht",
    "Gewässerschadenhaftpflicht",
    "Haus- und Grundstückshaftpflicht",
    "HV Privat",
    "Privathaftpflicht",
    "Privathaftpflicht für betreute Personen",
    "Produkt-Rückrufkosten",
    "Umwelthaftpflicht",
    "Umweltschaden",
    "Vermögensschaden",
    "Vertrauensschaden",
    "allgemeine Haftpflicht",
  ],
  Sonstige: ["Sonstige Schäden"],
  Hausrat: ["Hausrat", "Hausrat mit Feuer"],
};

// Reverse lookup: Sparte string → form key.
const SPARTE_TO_FORM = {};
Object.entries(SPARTE_FORM_ENTRIES).forEach(([formKey, spartes]) => {
  spartes.forEach((s) => {
    SPARTE_TO_FORM[s] = formKey;
  });
});

// Form key → wizard family (drives the broker step sequence).
const FORM_FAMILY = {
  A: "kfz",
  B: "kfz",
  C: "sach",
  D: "sach",
  E: "sach",
  F: "sach",
  G: "sach",
  H: "sach",
  I: "haftpflicht",
  Sonstige: "sach",
  Hausrat: "sach",
  X: "sach",
};

/** Form key for a Sparte string ('X' = unknown/fallback). */
export function formKeyOf(sparte) {
  return SPARTE_TO_FORM[sparte] || "X";
}

/** Wizard family for a Sparte string: 'kfz' | 'haftpflicht' | 'sach'. */
export function familyOf(sparte) {
  return FORM_FAMILY[formKeyOf(sparte)] || "sach";
}

export const isKasko = (sparte) => /Kasko/i.test(sparte || "");
export const isBetrieb = (sparte) => /Betrieb/i.test(sparte || "");

/** All known Sparte strings, grouped by family — for the Einordnung picker. */
export function spartenByFamily() {
  const out = { kfz: [], haftpflicht: [], sach: [] };
  Object.keys(SPARTE_TO_FORM).forEach((s) => {
    out[familyOf(s)].push(s);
  });
  return out;
}

export { SPARTE_FORM_ENTRIES };
